import React, { useState, useRef, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { sendAgentMessage } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";

const SUGGESTIONS = [
  "What QA checks failed for the most recent document?",
  "Summarize all failed checks and explain why they failed",
  "Which documentation issues are most commonly flagged?",
  "Show me all UNABLE TO DETERMINE results and their evidence",
];

function Message({ msg }) {
  return (
    <div className={`message ${msg.role}`}>
      <div className={`msg-avatar ${msg.role}`}>
        {msg.role === "ai" ? "AI" : "QA"}
      </div>
      <div style={{minWidth:0}}>
        <div className="msg-bubble" style={{lineHeight:1.65}}>{msg.role === "ai" ? renderMarkdown(msg.content) : msg.content}</div>
        {msg.sources?.length > 0 && (
          <div style={{display:"flex", gap:5, marginTop:5, flexWrap:"wrap"}}>
            {msg.sources.map((s,i) => (
              <span key={i} className="chip" style={{fontSize:10.5}}>
                <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/><path d="M9 1v4h4"/>
                </svg>
                {s.title}
              </span>
            ))}
          </div>
        )}
        <div className="msg-time">
          {new Date(msg.timestamp).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
        </div>
      </div>
    </div>
  );
}

export default function AgentChat() {
  const [params] = useSearchParams();
  const docId = params.get("docId");

  const [messages, setMessages] = useState([{
    id: "welcome",
    role: "ai",
    content: docId
      ? `Ready to analyze this document. I have access to the extracted content, QA check results, and audit trail. What would you like to know?`
      : `Hello. I'm the NeuroGen IOM QA Assistant. I can analyze IONM reports, explain QA check results, identify compliance gaps, and answer questions about any document in the system. How can I help?`,
    timestamp: new Date().toISOString(),
    sources: []
  }]);

  const [input, setInput]     = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef();
  const textareaRef = useRef();

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setMessages(m => [...m, { id:Date.now(), role:"user", content:text, timestamp:new Date().toISOString() }]);
    setInput("");
    setLoading(true);
    const history = messages.slice(-6).map(m => ({ role: m.role === "ai" ? "assistant" : "user", content: m.content }));
    try {
      const res = await sendAgentMessage(text, docId, history);
      setMessages(m => [...m, { id:Date.now()+1, role:"ai", content:res.message, timestamp:new Date().toISOString(), sources:res.sources||[] }]);
    } catch {
      setMessages(m => [...m, { id:Date.now()+1, role:"ai", content:"An error occurred. Please try again.", timestamp:new Date().toISOString(), sources:[] }]);
    }
    setLoading(false);
  };

  const onKey = e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">AI Assistant</div>
          <div className="page-subtitle">
            {docId ? `Context: document ${docId}` : "Powered by GPT-4.1 · Azure AI Foundry · RAG over indexed documents"}
          </div>
        </div>
        <div className="header-actions">
          <div className="chip" style={{gap:6, fontSize:11}}>
            <span style={{width:7,height:7,background:"var(--success)",borderRadius:"50%",display:"inline-block"}}/>
            Advisory only — reviewers make all compliance decisions
          </div>
        </div>
      </div>

      <div className="page-body" style={{ display:"flex", flexDirection:"column", height:"calc(100vh - 57px)", paddingBottom:0, overflow:"hidden" }}>
        <div style={{ display:"flex", flex:1, gap:16, overflow:"hidden", minHeight:0 }}>

          {/* Chat */}
          <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
            <div className="chat-messages" style={{flex:1, overflowY:"auto"}}>
              {messages.map(m => <Message key={m.id} msg={m}/>)}

              {loading && (
                <div className="message ai">
                  <div className="msg-avatar ai">AI</div>
                  <div className="msg-bubble" style={{display:"flex",gap:5,alignItems:"center",padding:"12px 14px"}}>
                    {[0,1,2].map(i => (
                      <span key={i} style={{
                        width:7, height:7, borderRadius:"50%", background:"var(--blue-400)", display:"inline-block",
                        animation:"pulse 1.2s ease infinite", animationDelay:`${i*0.2}s`
                      }}/>
                    ))}
                  </div>
                </div>
              )}
              <div ref={bottomRef}/>
            </div>

            {/* Input */}
            <div className="chat-input-area" style={{paddingBottom:20}}>
              {messages.length === 1 && (
                <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:10}}>
                  {SUGGESTIONS.map((s,i) => (
                    <button key={i} className="filter-pill" style={{fontSize:12, borderRadius:6}}
                      onClick={() => { setInput(s); textareaRef.current?.focus(); }}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div className="chat-input-row">
                <textarea
                  ref={textareaRef}
                  className="chat-input"
                  placeholder="Ask about QA results, failed checks, compliance trends..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={onKey}
                  rows={1}
                  disabled={loading}
                />
                <button className="btn btn-primary" onClick={send} disabled={loading || !input.trim()} style={{alignSelf:"flex-end"}}>
                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="3" y1="8" x2="13" y2="8" strokeLinecap="round"/>
                    <polyline points="9 4 13 8 9 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
              <div style={{marginTop:6, fontSize:11, color:"var(--text-tertiary)", fontFamily:"var(--font-mono)"}}>
                Enter to send · Shift+Enter for new line
              </div>
            </div>
          </div>

          {/* Side panel */}
          <div style={{width:220, flexShrink:0, display:"flex", flexDirection:"column", gap:12, overflowY:"auto", paddingBottom:20}}>
            <div className="card">
              <div className="card-header" style={{padding:"11px 14px"}}>
                <div className="card-title" style={{fontSize:12}}>Capabilities</div>
              </div>
              <div style={{padding:"8px 14px 12px"}}>
                {[
                  "Answer questions about specific documents",
                  "Search across all indexed documents",
                  "Explain failed QA checks with evidence",
                  "Summarize documents",
                  "Compare multiple documents",
                  "Identify compliance trends",
                ].map((item, i, arr) => (
                  <div key={i} style={{
                    display:"flex", gap:7, alignItems:"flex-start", padding:"5px 0",
                    fontSize:12, color:"var(--text-secondary)",
                    borderBottom: i < arr.length-1 ? "1px solid var(--border)" : "none"
                  }}>
                    <span style={{color:"var(--blue-500)", marginTop:1, flexShrink:0}}>·</span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header" style={{padding:"11px 14px"}}>
                <div className="card-title" style={{fontSize:12}}>Important Note</div>
              </div>
              <div style={{padding:"10px 14px 12px"}}>
                <p style={{fontSize:11.5, color:"var(--text-secondary)", lineHeight:1.65}}>
                  AI responses are advisory only. The assistant analyzes indexed documents and QA results but
                  <strong> cannot make pass/fail compliance decisions.</strong> All determinations must be made by a qualified QA reviewer.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}